import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  Select,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  usePrefersReducedMotion,
  useToast,
} from '@chakra-ui/react';
import { Breadcrumbs, Button, showToast } from '@diamond/shared/ui';
import { AdminLayout } from '@diamond/sol-admin/components';
import {
  AddOutlined,
  DeleteOutline,
  UploadFileOutlined,
} from '@mui/icons-material';
import { isAxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ExclusionRow, ExclusionRule } from '../api';
import {
  EMPTY_FIELDS,
  ExclusionFields,
  ExclusionFieldsValue,
  fieldsFromQuery,
  queryFromFields,
  ruleFromFields,
} from '../components/exclusion-fields';
import { ImportDrawer } from '../components/import-drawer';
import {
  DisplayExclusionRow,
  useExclusionLabels,
} from '../hooks/use-exclusion-labels';
import { useExclusions } from '../hooks/use-exclusions';
import { mutationErrorMessage } from '../utils/errors';
import { readExclusionQuery, writeExclusionQuery } from '../utils/query';

function CodeLabel({
  code,
  label,
}: {
  code: string | number;
  label: string | null;
}) {
  return (
    <Stack spacing={0.5}>
      <Text fontWeight="medium">{label || 'Name unavailable'}</Text>
      <Text
        fontSize="sm"
        color="gray.600"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {code}
      </Text>
    </Stack>
  );
}
function RuleScope({ row }: { row: DisplayExclusionRow }) {
  if (row.cg_1_business_segmentation === null)
    return (
      <Badge
        bg="gray.100"
        color="gray.700"
        borderRadius="md"
        px={2}
        py={1}
        whiteSpace="normal"
      >
        All ship-tos (any CG1)
      </Badge>
    );
  return (
    <CodeLabel
      code={row.cg_1_business_segmentation}
      label={row.cg_1_business_segmentation_desc}
    />
  );
}

const EMPTY_ROWS: ExclusionRow[] = [];

export function ExcludeBranchPage() {
  const [params, setParams] = useSearchParams();
  const queryString = params.toString();
  const query = readExclusionQuery(params);
  const { list, create, bulkCreate, remove, bulkRemove, refresh } =
    useExclusions(query);
  const [filters, setFilters] = useState<ExclusionFieldsValue>(() =>
    fieldsFromQuery(query)
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState<ExclusionFieldsValue>(EMPTY_FIELDS);
  const [deleting, setDeleting] = useState<DisplayExclusionRow[]>([]);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [importError, setImportError] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const reducedMotion = usePrefersReducedMotion();
  const rows = useExclusionLabels(list.data?.data ?? EMPTY_ROWS);
  const pagination = list.data?.pagination;
  const pending =
    create.isPending ||
    bulkCreate.isPending ||
    remove.isPending ||
    bulkRemove.isPending;
  const accessError = [
    create.error,
    bulkCreate.error,
    remove.error,
    bulkRemove.error,
  ].find(
    (error) =>
      isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0)
  );
  const canWrite = list.isSuccess && !accessError;
  const hasFilters = !!(
    query.product_item_code ||
    query.distribution_channel ||
    query.plant ||
    query.cg_1_business_segmentation
  );

  useEffect(() => {
    setSelected([]);
    setDeleting([]);
    setFilters(
      fieldsFromQuery(readExclusionQuery(new URLSearchParams(queryString)))
    );
  }, [queryString]);
  useEffect(() => {
    setSelected((current) =>
      current.filter((id) => rows.some((row) => row.id === id))
    );
  }, [rows]);
  const handleError = async (
    error: unknown,
    report: (message: string) => void
  ) => {
    report(mutationErrorMessage(error));
    await refresh();
  };
  const handleCreate = async () => {
    if (!canWrite || pending) return;
    const body = ruleFromFields(form);
    if (!body) {
      setFormError('Choose a product, distribution channel, and branch.');
      return;
    }
    setFormError('');
    try {
      await create.mutateAsync(body);
      setAdding(false);
      setForm(EMPTY_FIELDS);
      setSelected([]);
      showToast(toast, 'success', 'Exclusion added.');
      await refresh();
    } catch (error) {
      await handleError(error, setFormError);
    }
  };
  const handleImport = async (items: ExclusionRule[]) => {
    if (!canWrite || pending) return;
    setImportError('');
    try {
      const result = await bulkCreate.mutateAsync(items);
      setImporting(false);
      setSelected([]);
      showToast(
        toast,
        'success',
        `${result.data.created} added · ${result.data.unchanged} unchanged.`
      );
      await refresh();
    } catch (error) {
      await handleError(error, setImportError);
    }
  };
  const reconcileRemoval = async () => {
    const result = await list.refetch();
    if (result.isSuccess) {
      const lastPage = Math.max(1, result.data.pagination.total_pages);
      if ((query.page ?? 1) > lastPage) {
        setParams(writeExclusionQuery({ ...query, page: lastPage }));
      }
    }
  };
  const handleRemove = async () => {
    if (!canWrite || pending || !deleting.length) return;
    setError('');
    try {
      if (deleting.length === 1) await remove.mutateAsync(deleting[0].id);
      else await bulkRemove.mutateAsync(deleting.map((row) => row.id));
      showToast(toast, 'success', 'Exclusions removed.');
      setDeleting([]);
      setSelected([]);
      await reconcileRemoval();
    } catch (error) {
      setError(mutationErrorMessage(error));
      await reconcileRemoval();
      if (
        deleting.length === 1 &&
        isAxiosError(error) &&
        error.response?.status === 404
      ) {
        setDeleting([]);
      }
    }
  };
  const selectAll = selected.length === rows.length && rows.length > 0;

  return (
    <AdminLayout>
      <Box pb={4}>
        <Breadcrumbs
          links={[
            { title: 'Home', link: '/' },
            { title: 'Exclude Branch', link: '/exclude-branch' },
          ]}
          useRouterDom
        />
      </Box>
      <Flex
        justify="space-between"
        align="flex-start"
        gap={4}
        wrap="wrap"
        mb={6}
      >
        <Box>
          <Heading size="lg">Exclude Branch</Heading>
          <Text color="gray.600" mt={2}>
            Manage where products are excluded from sale, by branch and ship-to
            scope.
          </Text>
        </Box>
        <HStack flexWrap="wrap" spacing={3}>
          <Button
            variant="outline"
            leftIcon={<UploadFileOutlined fontSize="small" />}
            onClick={() => {
              setImportError('');
              setImporting(true);
            }}
            isDisabled={!canWrite || pending}
          >
            Import Excel
          </Button>
          <Button
            variant="solid"
            leftIcon={<AddOutlined fontSize="small" />}
            onClick={() => {
              setFormError('');
              setForm(EMPTY_FIELDS);
              setAdding(true);
            }}
            isDisabled={!canWrite || pending}
          >
            Add exclusion
          </Button>
        </HStack>
      </Flex>
      <Stack spacing={5} pb={12}>
        {!!accessError && (
          <Alert status="warning" borderRadius="md" alignItems="flex-start">
            <AlertIcon />
            <Box flex={1}>
              <Text fontWeight="semibold">Editing unavailable</Text>
              <Text fontSize="sm" mt={1}>
                The server denied editing. Reload this page after your access or
                the editing mode changes. You can still browse exclusions.
              </Text>
            </Box>
          </Alert>
        )}
        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          p={{ base: 4, md: 5 }}
          bg="white"
        >
          <Text fontWeight="semibold" mb={4}>
            Find exclusions
          </Text>
          <ExclusionFields
            prefix="filter"
            value={filters}
            onChange={setFilters}
            disabled={pending}
          />
          <HStack mt={4} spacing={3}>
            <Button
              variant="solid"
              size="sm"
              onClick={() =>
                setParams(
                  writeExclusionQuery({
                    ...queryFromFields(filters),
                    page: 1,
                    limit: query.limit,
                  })
                )
              }
              isDisabled={pending}
            >
              Apply filters
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilters(EMPTY_FIELDS);
                setParams(writeExclusionQuery({ page: 1, limit: query.limit }));
              }}
              isDisabled={pending}
            >
              Reset
            </Button>
          </HStack>
        </Box>
        {error && deleting.length === 0 && (
          <Alert status="error" role="alert">
            <AlertIcon />
            {error}
          </Alert>
        )}
        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          bg="white"
          overflow="hidden"
        >
          <Flex
            justify="space-between"
            align="center"
            p={4}
            gap={3}
            wrap="wrap"
            borderBottom="1px solid"
            borderColor="gray.200"
          >
            <Text
              fontWeight="semibold"
              aria-live="polite"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {selected.length
                ? `${selected.length} selected`
                : `${pagination?.total ?? 0} exclusions`}
            </Text>
            {selected.length > 0 && (
              <Button
                variant="outline"
                color="red.700"
                borderColor="red.600"
                _hover={{
                  bg: 'red.50',
                  color: 'red.800',
                  borderColor: 'red.700',
                }}
                _active={{
                  bg: 'red.100',
                  color: 'red.900',
                  borderColor: 'red.700',
                }}
                size="sm"
                leftIcon={<DeleteOutline fontSize="small" />}
                onClick={() => {
                  setError('');
                  setDeleting(rows.filter((row) => selected.includes(row.id)));
                }}
                isDisabled={!canWrite || pending}
              >
                Remove selected
              </Button>
            )}
            {list.isFetching && !list.isPending && (
              <Text fontSize="sm" role="status" color="gray.600">
                Refreshing…
              </Text>
            )}
          </Flex>
          {list.isPending && (
            <Stack
              p={5}
              spacing={4}
              role="status"
              aria-label="Loading exclusions"
            >
              {[1, 2, 3, 4].map((row) => (
                <Skeleton key={row} height="48px" />
              ))}
            </Stack>
          )}
          {list.isError && (
            <Stack p={8} align="center">
              <Text role="alert">Could not load exclusions.</Text>
              <Button variant="outline" onClick={() => list.refetch()}>
                Retry list
              </Button>
            </Stack>
          )}
          {list.isSuccess && rows.length === 0 && (
            <Stack p={10} align="center" textAlign="center">
              <Heading size="sm">
                {hasFilters ? 'No matching exclusions' : 'No exclusions yet'}
              </Heading>
              <Text color="gray.600">
                {hasFilters
                  ? 'Try changing or resetting your filters.'
                  : 'Add a rule to exclude a product for a branch.'}
              </Text>
            </Stack>
          )}
          {list.isSuccess && rows.length > 0 && (
            <Box overflowX="auto">
              <Table size="md" minW="980px">
                <Thead bg="gray.50">
                  <Tr>
                    <Th w="48px">
                      <Checkbox
                        aria-label="Select all rules on this page"
                        isChecked={selectAll}
                        isIndeterminate={selected.length > 0 && !selectAll}
                        isDisabled={!canWrite || pending}
                        onChange={(event) =>
                          setSelected(
                            event.target.checked
                              ? rows.map((row) => row.id)
                              : []
                          )
                        }
                      />
                    </Th>
                    {[
                      'Product',
                      'Branch / plant',
                      'Distribution channel',
                      'CG1 scope',
                      'Updated',
                      'Action',
                    ].map((heading) => (
                      <Th
                        key={heading}
                        textTransform="none"
                        letterSpacing="normal"
                      >
                        {heading}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row) => (
                    <Tr
                      key={row.id}
                      bg={selected.includes(row.id) ? 'gray.50' : undefined}
                      _hover={{ bg: 'gray.50' }}
                    >
                      <Td>
                        <Checkbox
                          aria-label={`Select exclusion ${
                            row.product_item_code
                          } ${row.plant} ${
                            row.cg_1_business_segmentation ?? 'all CG1'
                          }`}
                          isChecked={selected.includes(row.id)}
                          isDisabled={!canWrite || pending}
                          onChange={(event) =>
                            setSelected((current) =>
                              event.target.checked
                                ? [...current, row.id]
                                : current.filter((id) => id !== row.id)
                            )
                          }
                        />
                      </Td>
                      <Td maxW="280px">
                        <CodeLabel
                          code={row.product_item_code}
                          label={row.product_title}
                        />
                      </Td>
                      <Td>
                        <CodeLabel code={row.plant} label={row.plant_name} />
                      </Td>
                      <Td>
                        <CodeLabel
                          code={row.distribution_channel}
                          label={row.distribution_channel_desc}
                        />
                      </Td>
                      <Td>
                        <RuleScope row={row} />
                      </Td>
                      <Td>
                        <Text
                          fontSize="sm"
                          whiteSpace="nowrap"
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {new Intl.DateTimeFormat('en-GB', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                            timeZone: 'Asia/Jakarta',
                          }).format(new Date(row.updated_at))}
                        </Text>
                      </Td>
                      <Td>
                        <Button
                          variant="ghost"
                          color="red.700"
                          bg="transparent"
                          _hover={{ bg: 'red.50', color: 'red.800' }}
                          _active={{ bg: 'red.100', color: 'red.900' }}
                          size="sm"
                          minH="40px"
                          aria-label={`Remove exclusion ${row.product_item_code} ${row.plant}`}
                          onClick={() => {
                            setError('');
                            setDeleting([row]);
                          }}
                          isDisabled={!canWrite || pending}
                        >
                          Remove
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
          <Flex
            px={4}
            py={3}
            justify="space-between"
            align="center"
            wrap="wrap"
            gap={3}
            borderTop="1px solid"
            borderColor="gray.200"
          >
            <HStack>
              <Text fontSize="sm" as="label" htmlFor="exclusion-page-size">
                Rows per page
              </Text>
              <Select
                id="exclusion-page-size"
                size="sm"
                w="80px"
                value={query.limit}
                onChange={(event) =>
                  setParams(
                    writeExclusionQuery({
                      ...query,
                      page: 1,
                      limit: Number(event.target.value),
                    })
                  )
                }
                isDisabled={pending}
              >
                {[20, 50, 100].map((limit) => (
                  <option key={limit}>{limit}</option>
                ))}
              </Select>
            </HStack>
            <HStack>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setParams(
                    writeExclusionQuery({
                      ...query,
                      page: (query.page ?? 1) - 1,
                    })
                  )
                }
                isDisabled={pending || (query.page ?? 1) <= 1}
              >
                Previous
              </Button>
              <Text fontSize="sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                Page {query.page} of {Math.max(1, pagination?.total_pages ?? 1)}
              </Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setParams(
                    writeExclusionQuery({
                      ...query,
                      page: (query.page ?? 1) + 1,
                    })
                  )
                }
                isDisabled={
                  pending ||
                  !pagination ||
                  (query.page ?? 1) >= pagination.total_pages
                }
              >
                Next
              </Button>
            </HStack>
          </Flex>
        </Box>
      </Stack>
      <Drawer
        isOpen={adding}
        onClose={() => !pending && setAdding(false)}
        placement="right"
        size="md"
        closeOnEsc={!pending}
        closeOnOverlayClick={!pending}
      >
        <DrawerOverlay />
        <DrawerContent
          motionProps={
            reducedMotion ? { transition: { duration: 0 } } : undefined
          }
        >
          <DrawerCloseButton isDisabled={pending} />
          <DrawerHeader>Add exclusion</DrawerHeader>
          <DrawerBody>
            <Text mb={6} color="gray.600">
              Choose the product and branch, then define which ship-tos the rule
              applies to.
            </Text>
            {formError && (
              <Alert status="error" role="alert" mb={4}>
                <AlertIcon />
                {formError}
              </Alert>
            )}
            <ExclusionFields
              prefix="add"
              value={form}
              onChange={setForm}
              required
              disabled={pending}
            />
          </DrawerBody>
          <DrawerFooter gap={3}>
            <Button
              variant="outline"
              onClick={() => setAdding(false)}
              isDisabled={pending}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              onClick={handleCreate}
              isDisabled={!canWrite}
              isLoading={create.isPending}
            >
              Add rule
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      {importing && (
        <ImportDrawer
          isOpen
          onClose={() => setImporting(false)}
          onSubmit={handleImport}
          pending={bulkCreate.isPending}
          canWrite={canWrite}
          serverError={importError}
        />
      )}
      <AlertDialog
        isOpen={deleting.length > 0}
        onClose={() => !pending && setDeleting([])}
        leastDestructiveRef={cancelRef}
        motionPreset={reducedMotion ? 'none' : 'scale'}
        closeOnEsc={!pending}
        closeOnOverlayClick={!pending}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              Remove{' '}
              {deleting.length === 1
                ? 'exclusion'
                : `${deleting.length} exclusions`}
              ?
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={4}>
                These rules will be removed. Other product availability checks
                still apply.
              </Text>
              <Stack spacing={3} maxH="300px" overflowY="auto">
                {deleting.map((row) => (
                  <Box
                    key={row.id}
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    pb={3}
                  >
                    <Text fontWeight="medium">
                      {row.product_title || row.product_item_code}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {row.product_item_code} · {row.plant_name || row.plant} ·{' '}
                      {row.distribution_channel}
                    </Text>
                    <Box mt={1}>
                      <RuleScope row={row} />
                    </Box>
                  </Box>
                ))}
              </Stack>
              {error && (
                <Alert mt={4} status="error" role="alert">
                  <AlertIcon />
                  {error}
                </Alert>
              )}
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button
                ref={cancelRef}
                variant="outline"
                onClick={() => setDeleting([])}
                isDisabled={pending}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                bg="red.600"
                color="white"
                borderColor="red.600"
                _hover={{ bg: 'red.700' }}
                _active={{ bg: 'red.800' }}
                onClick={handleRemove}
                isDisabled={!canWrite}
                isLoading={remove.isPending || bulkRemove.isPending}
              >
                Remove exclusion{deleting.length > 1 ? 's' : ''}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </AdminLayout>
  );
}
